'use client';

import { CaretUp } from 'assets/icons';
import { useEffect, useRef, useState } from 'react';
import { Container } from '..';
import { getSpeedsizeImageUrl } from 'components/shared/custom-image/CustomImage';
import { analytics } from 'lib/analytics/analytics';
import { useTranslations } from 'next-intl';
import styles from './Capabilities.module.css';

const cx = (...classes: (string | undefined)[]) =>
  classes.filter(Boolean).join(' ');

interface CapabilityProps {
  className?: string;
  title: string | React.ReactNode;
  $gridSpan: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  description: string | React.ReactNode;
  image: {
    url: string;
    height: number;
    width: number;
    resp?: string;
    bottom?: boolean;
  };
  $background: string;
  $color?: string;
  $boxShadow?: string;
  $mixBlendMode?: string;
  $marginBottom?: number;
  stretch?: boolean;
  $opacity?: number;
  $delay?: number;
}

interface StyledCapabilityProps {
  $background: string;
  $gridSpan: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  $delay?: number;
  $color?: string;
  $boxShadow?: string;
  $mixBlendMode?: string;
  $marginBottom?: number;
  $opacity?: number;
}

const useCapabilities = () => {
  const t = useTranslations('ring');
  return {
    longevity: {
      // title: 'Longevity ',
      // heading: 'Engineer your Longevity',
      // description:
      //   'Leverage the Ring AIR’s cutting-edge, passive and preventive health monitoring capabilities to set yourself on a path toward extended vitality and your quest for a longer, healthier life.',
      title: t('ring.capabilities.longevity.tab'),
      heading: t('ring.capabilities.longevity.head'),
      description: t('ring.capabilities.longevity.subHead'),
      features: [
        {
          // title: 'AFib Detection',
          title: t('ring.capabilities.longevity.afibCard.title'),
          $gridSpan: 6,
          // description:
          //   'This PowerPlug discreetly monitors your heart rhythm every night, detecting early signs of AFib (Atrial Fibrillation). Enjoy seamless and nocturnal monitoring with daily medical-grade reports for peace of mind.',
          description: t('ring.capabilities.longevity.afibCard.description'),
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/afib.png',
            resp: 'web_v2/ring-buy/capabilities-v2/responsive/afib.png',
            height: 196,
            width: 298,
          },
          $background:
            'radial-gradient(114.96% 93.08% at 72.06% 58.95%, #000 0%, #3C001A 100%)',
        },
        {
          // title: 'Cardio Adaptability',
          title: t('ring.capabilities.longevity.cardioAdaptabilityCard.title'),
          $gridSpan: 6,
          // description:
          //   'Monitor your heart's activity every night with this PowerPlug. It analyzes cardio adaptability using detailed tachograms and Lorenz plots, offering key insights into how your heart responds to activities, stress, and rest.',
          description: t(
            'ring.capabilities.longevity.cardioAdaptabilityCard.description',
          ),
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/cardio.png',
            resp: 'web_v2/ring-buy/capabilities-v2/responsive/cardio.png',
            height: 1613,
            width: 1168,
          },
          $background:
            'radial-gradient(86.82% 80.51% at 48.84% 61.96%, #000 0%, #035231 100%);',
          $boxShadow: '0px 0px 2px 0px rgba(194, 63, 120, 0.50) inset;',
        },
      ],
    },
    productivity: {
      // title: 'Productivity',
      // heading: 'Want superhuman productivity?',
      // description:
      //   'Unlock your body’s hidden potential and crush your goals, day after day. Experience Ring AIR’s revolutionary approach to performance and productivity, keeping you at your best mentally and physically.',
      title: t('ring.capabilities.productivity.tab'),
      heading: t('ring.capabilities.productivity.head'),
      description: t('ring.capabilities.productivity.subHead'),
      features: [
        {
          // title: 'Circadian Alignment',
          $gridSpan: 3,
          title: t('ring.capabilities.productivity.circadianCard.title'),
          description: t(
            'ring.capabilities.productivity.circadianCard.description',
          ),
          // description:
          //   'Suggests optimal times to expose yourself to natural sunlight, exercise, and wind down in the evening, aiming to improve your energy levels, and sleep quality.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/phase-alignment.png',
            resp: 'web_v2/ring-buy/capabilities-v2/phase-alignment.png',
            height: 406,
            width: 292,
            bottom: true,
          },
          $background:
            'radial-gradient(139% 150.68% at 55.13% 100%, rgba(0, 0, 0, 0.40) 0%, #00B6FF 100%), #000',
          $boxShadow: '0px 0px 2.086px 0px rgba(0, 78, 22, 0.50) inset',
          $marginBottom: -94,
        },
        {
          // title: 'Caffeine Window ',
          $gridSpan: 3,
          title: t('ring.capabilities.productivity.caffeineCard.title'),
          description: t(
            'ring.capabilities.productivity.caffeineCard.description',
          ),
          // description:
          //   'Recommends the best times for caffeine consumption based on your sleep, to boost your alertness throughout the day without affecting the quality of your sleep.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/caffeine.png',
            resp: 'web_v2/ring-buy/capabilities-v2/caffeine.png',
            height: 406,
            width: 292,
          },
          $background:
            'radial-gradient(139% 150.68% at 55.13% 100%, rgba(0, 0, 0, 0.40) 0%, #7FFFC9 100%), #000',
          $boxShadow: '0px 0px 2.086px 0px rgba(0, 78, 22, 0.50) inset;',
        },
        {
          // title: 'Vitamin D',
          title: t('ring.capabilities.productivity.vitaminDCard.title'),
          description: t(
            'ring.capabilities.productivity.vitaminDCard.description',
          ),
          $gridSpan: 3,
          // description:
          //   'Calculates optimal times for you to step outside, based on your skin type, location, and current UV index, ensuring you get the most effective and safe sun exposure.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/vitamin-d.png',
            resp: 'web_v2/ring-buy/capabilities-v2/vitamin-d.png',
            height: 406,
            width: 292,
          },
          $background:
            'radial-gradient(67.91% 71.81% at 50% 28.19%, #EEB657 0%, #B64200 93.4%), #EEE',
        },
        {
          // title: 'Screentime',
          $gridSpan: 3,
          title: t('ring.capabilities.productivity.screenTimeCard.title'),
          description: t(
            'ring.capabilities.productivity.screenTimeCard.description',
          ),
          // description:
          //   'Tracks screen time and its impact on sleep, offering personalized insights for healthier habits.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/Screentime_updated.png',
            resp: 'web_v2/ring-buy/capabilities-v2/Screentime_updated.png',
            height: 406,
            width: 292,
          },
          $background:
            'radial-gradient(139% 150.68% at 55.13% 100%, #121429 0%, #4B5FFF 100%), #000',
          $mixBlendMode: 'luminosity',
          $opacity: 0.7,
          $boxShadow: '0px 0px 2.086px 0px rgba(0, 78, 22, 0.50) inset',
        },
      ],
    },
    movement: {
      // title: 'Movement',
      // heading: 'Capture every movement',
      // description:
      //   'The Ring continuously monitors your physical activity throughout the day using advanced motion sensors. It tracks various types of movement, from light activities like walking to more intense exercises, providing a comprehensive view of your daily activity levels.',
      title: t('ring.capabilities.movement.tab'),
      heading: t('ring.capabilities.movement.head'),
      description: t('ring.capabilities.movement.subHead'),
      features: [
        {
          // title: 'Steps, Distance, Calories',
          $gridSpan: 7,
          title: t('ring.capabilities.movement.stepsDistCalCard.title'),
          description: t(
            'ring.capabilities.movement.stepsDistCalCard.description',
          ),
          // description:
          //   'Track your daily movement seamlessly. Real-time monitoring of steps taken, distance covered, and calories burned provides comprehensive insights into your activity levels, motivating you towards a more active lifestyle.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/movement.png',
            resp: 'web_v2/ring-buy/capabilities-v2/movement-2.png',
            height: 406,
            width: 292,
          },
          $background:
            'radial-gradient(139% 150.68% at 55.13% 100%, rgba(0, 0, 0, 0.40) 0%, #FE6341 100%), #000',
          $boxShadow: '0px 0px 2.086px 0px rgba(0, 78, 22, 0.50) inset',
        },
        {
          // title: 'VO2 Max tracking',
          $gridSpan: 5,
          // description:
          //   'Unlock your cardiovascular potential. Measure your VO2 Max, your body's oxygen utilization during exercise, and assess post-workout recovery. Gain valuable insights to optimize your fitness routine and enhance overall cardiovascular health.',
          title: t('ring.capabilities.movement.vO2Card.title'),
          description: t('ring.capabilities.movement.vO2Card.description'),
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/vo2.png',
            resp: 'web_v2/ring-buy/capabilities-v2/vo2.png',
            height: 406,
            width: 292,
            bottom: true,
          },
          $background:
            'radial-gradient(139% 150.68% at 55.13% 100%, rgba(0, 0, 0, 0.40) 0%, #3A041C 100%), #000',
          $marginBottom: -26,
        },
      ],
    },
    sleep: {
      // title: 'Sleep',
      // heading: 'Enjoy sleep that dreams are made of',
      // description:
      //   "Discover the key to sleeping better. Get an everyday sleep score that shows how well you've rested. Track your sleep stages, learn what affects your sleep quality, and unlock personalized tools for better nights. Wake up refreshed and ready for anything.",
      title: t('ring.capabilities.sleep.tab'),
      heading: t('ring.capabilities.sleep.head'),
      description: t('ring.capabilities.sleep.subHead'),
      features: [
        {
          // title: 'Sleep Score',
          $gridSpan: 4,
          title: t('ring.capabilities.sleep.scoreCard.title'),
          description: t('ring.capabilities.sleep.scoreCard.description'),
          // description:
          //   'Your Sleep Score, out of 100 is based on trackable Sleep Contributors and the time you spent in different Sleep Stages. ',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/sleep.png',
            resp: 'web_v2/ring-buy/capabilities-v2/sleep.png',
            height: 406,
            width: 292,
          },
          // web_v2/ring-buy/capabilities-v2/bg.png
          $background: `radial-gradient(94.99% 109.16% at 74.63% 44.57%, #012632 0%, #018138 100%);`,
        },
        {
          // title: 'Sleep Stages',
          $gridSpan: 4,
          title: t('ring.capabilities.sleep.stagesCard.title'),
          description: t('ring.capabilities.sleep.stagesCard.description'),
          // description:
          //   'Track your sleep and duration of your sleep across sleep stages like REM, Light and Deep Sleep.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/sleep-stages.png',
            resp: 'web_v2/ring-buy/capabilities-v2/sleep-stages.png',
            height: 406,
            width: 292,
          },
          $background:
            'linear-gradient(180deg, rgba(75, 66, 79, 0.68) 0%, rgba(46, 37, 47, 0.35) 100%), #000',
          stretch: true,
        },
        {
          // title: 'The nuances of your sleep, captured',
          $gridSpan: 4,
          title: t('ring.capabilities.sleep.nuancesCard.title'),
          description: t('ring.capabilities.sleep.nuancesCard.description'),
          // description:
          //   'Ring AIR tracks Sleep Debt, Morning Alertness, and Tosses and Turns, providing insights for better sleep and productivity through personalized adjustments.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/sleep-debt.png',
            resp: 'web_v2/ring-buy/capabilities-v2/sleep-debt.png',
            height: 406,
            width: 292,
          },
          $background:
            'radial-gradient(49.08% 62.38% at 94.82% 68.54%, #2C495D 0%, #050A1A 100%)',
          stretch: true,
        },
      ],
    },
    recovery: {
      // title: 'Recovery',
      // heading: 'Dynamic Recovery: The edge in your recovery game.',
      // description:
      //   'Dynamic Recovery analyzes key physiological markers, delivering a real-time adaptive Recovery Score. Gain insights into how sleep, Stress Rhythm, Skin Temperature, and Heart Rate variability (HRV) influence your body’s restoration. Enhance your recovery consciously through power naps and breathwork techniques.',
      title: t('ring.capabilities.recovery.tab'),
      heading: t('ring.capabilities.recovery.head'),
      description: t('ring.capabilities.recovery.subHead'),
      features: [
        {
          // title: 'Dynamic Recovery Score',
          $gridSpan: 4,
          title: t('ring.capabilities.recovery.scoreCard.title'),
          description: t('ring.capabilities.recovery.scoreCard.description'),
          // description:
          //   'A single score reflects your body's daily recovery. It's based on biomarkers like HRV, RHR, Stress Rhythm, and Skin Temperature, which indicate your recovery state.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/recovery.png',
            resp: 'web_v2/ring-buy/capabilities-v2/recovery.png',
            height: 406,
            width: 292,
          },
          $background: `radial-gradient(118.21% 134.77% at 100% 100%, #1B3355 0%, #24374F 33.49%, #7D5918 100%);`,
        },
        {
          // title: 'Stress Rhythm',
          $gridSpan: 4,
          title: t('ring.capabilities.recovery.stressCard.title'),
          description: t('ring.capabilities.recovery.stressCard.description'),
          // description:
          //   'Stress Rhythm shows if you're stimulated, relaxed, or stressed by analyzing HR, RHR, and HRV variations throughout your circadian rhythm.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/stress.png',
            resp: 'web_v2/ring-buy/capabilities-v2/stress.png',
            height: 406,
            width: 292,
          },
          $background: '#121212',
        },
        {
          // title: 'HRV Interpretation',
          $gridSpan: 4,
          title: t('ring.capabilities.recovery.hrvCard.title'),
          description: t('ring.capabilities.recovery.hrvCard.description'),
          // description:
          //   'Real time changes with respect to HRV and actionable insights into how it can be improved.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/hrv-spo2.png',
            resp: 'web_v2/ring-buy/capabilities-v2/hrv-spo2.png',
            height: 406,
            width: 292,
            bottom: true,
          },
          $background: '#fff',
          $color: '#000000',
          stretch: true,
        },
      ],
    },
    'womens-health': {
      // title: 'Women’s Health',
      // heading: 'Attuned to Women’s distinct health needs',
      // description:
      //   'Personalized health insights for every stage of the unique journey of womanhood, helping you learn about your body and proactively manage your health.',
      title: t('ring.capabilities.womenHealth.tab'),
      heading: t('ring.capabilities.womenHealth.head'),
      description: t('ring.capabilities.womenHealth.subHead'),
      features: [
        {
          // title: 'Cycle Tracking',
          $gridSpan: 4,
          title: t('ring.capabilities.womenHealth.cycleCard.title'),
          description: t('ring.capabilities.womenHealth.cycleCard.description'),
          // description:
          //   'Cycle Tracking offers a comprehensive overview of your menstrual cycle phases and predicts upcoming cycles with high accuracy. This tool provides personalized insights into your fertility windows and symptom patterns, enabling proactive health management and lifestyle adjustments. Empower yourself with knowledge to make informed decisions about your reproductive health.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/cycle-tracking-3.png',
            resp: 'web_v2/ring-buy/capabilities-v2/cycle-tracking-3.png',
            height: 406,
            width: 292,
            bottom: true,
          },
          $background:
            'radial-gradient(114.96% 93.08% at 72.06% 58.95%, #000 0%, #710007 100%)',
          $marginBottom: -26,
        },
        {
          // title: 'Cycle Tracking',
          $gridSpan: 4,
          title: t('ring.capabilities.womenHealth.cycleProCard.title'),
          description: t(
            'ring.capabilities.womenHealth.cycleProCard.description',
          ),
          // description:
          //   'Cycle Tracking offers a comprehensive overview of your menstrual cycle phases and predicts upcoming cycles with high accuracy. This tool provides personalized insights into your fertility windows and symptom patterns, enabling proactive health management and lifestyle adjustments. Empower yourself with knowledge to make informed decisions about your reproductive health.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/cno-pro.png',
            resp: 'web_v2/ring-buy/capabilities-v2/cno-pro.png',
            height: 406,
            width: 292,
            bottom: true,
          },
          $background:
            'radial-gradient(109.58% 97.86% at 50% 58.66%, #000 0%, #0A0001 22.3%, #452B7D 100%)',
          $marginBottom: -26,
        },
        {
          // title: 'Pregnancy Insights',
          $gridSpan: 4,
          title: t('ring.capabilities.womenHealth.pregnancyCard.title'),
          description: t(
            'ring.capabilities.womenHealth.pregnancyCard.description',
          ),
          // description:
          //   'A precise ovulation tracker that helps you predict fertile windows and plan for pregnancy.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/pregnancy-1.png',
            resp: 'web_v2/ring-buy/capabilities-v2/pregnancy-1.png',
            height: 406,
            width: 292,
          },
          $background:
            'radial-gradient(139% 150.68% at 55.13% 100%, #000 0%, #080C1E 42.4%, #405BE6 100%), #000',
          $boxShadow: '0px 0px 2.086px 0px rgba(0, 78, 22, 0.50) inset',
        },
      ],
    },
    ecosystem: {
      // title: 'Ecosystem',
      // heading: 'The ever expanding health ecosystem',
      // description:
      //   'Our unique suite of hardware and software products work in perfect harmony to provide you with a dashboard of your health. ',
      title: t('ring.capabilities.ecosystem.tab'),
      heading: t('ring.capabilities.ecosystem.head'),
      description: t('ring.capabilities.ecosystem.subHead'),
      features: [
        {
          // title: 'Ultrahuman M1',
          $gridSpan: 4,
          title: t('ring.capabilities.ecosystem.m1Card.title'),
          description: t('ring.capabilities.ecosystem.m1Card.description'),
          // description:
          //   'Optimize your metabolic fitness with continuous glucose tracking and real time actionable insights. ',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/m1.png',
            resp: 'web_v2/ring-buy/capabilities-v2/m1.png',
            height: 707,
            width: 600,
          },
          $background: '#FFF',
          $color: '#000000',
          stretch: false,
        },
        {
          // title: 'Blood Vision',
          $gridSpan: 4,
          title: t('ring.capabilities.ecosystem.bloodVisionCard.title'),
          description: t(
            'ring.capabilities.ecosystem.bloodVisionCard.description',
          ),
          // description:
          //   'A radical new way to understand and interpret 100+ blood markers in the context of your lifestyle factors and longevity. ',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/bv.png',
            resp: 'web_v2/ring-buy/capabilities-v2/bv.png',
            height: 406,
            width: 292,
          },
          $background: '#141414',
          // background:
          //   'radial-gradient(100.06% 100.23% at 50% 100%, rgba(192, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%), #000',
          $boxShadow: '0px 0px 2.086px 0px rgba(0, 78, 22, 0.50) inset',
          $mixBlendMode: 'plus-lighter',
        },
        {
          // title: 'Ultrahuman Home',
          $gridSpan: 4,
          title: t('ring.capabilities.ecosystem.homeCard.title'),
          description: t('ring.capabilities.ecosystem.homeCard.description'),
          // description:
          //   'A silent and sophisticated home health device that monitors environmental markers that affect your health every day.',
          image: {
            url: 'web_v2/ring-buy/capabilities-v2/home.png',
            resp: 'web_v2/ring-buy/capabilities-v2/home.png',
            height: 406,
            width: 292,
          },
          $background: '#FFF',
          $color: '#000000',
          $marginBottom: -26,
        },
      ],
    },
  };
};

const StyledCapability = ({
  $background,
  $gridSpan,
  $delay,
  $color,
  $boxShadow,
  $mixBlendMode,
  $marginBottom,
  $opacity,
  className,
  children,
  ...rest
}: StyledCapabilityProps &
  React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cx(styles.styledCapability, className)}
    style={
      {
        ['--sc-bg']: $background,
        ['--sc-gridspan']: $gridSpan,
        ['--sc-delay']: `${$delay || 0.0}s`,
        ['--sc-boxshadow']: $boxShadow ?? 'none',
        ['--sc-mixblend']: $mixBlendMode ?? 'normal',
        ['--sc-mb']: $marginBottom ? `${$marginBottom}px` : '0',
        ['--sc-color']: $color,
      } as React.CSSProperties
    }
    {...rest}
  >
    {children}
  </div>
);

export const Capability = (props: CapabilityProps) => {
  return (
    <StyledCapability
      $background={props.$background}
      $gridSpan={props.$gridSpan}
      $delay={props.$delay}
      $color={props.$color}
      $boxShadow={props.$boxShadow}
      $mixBlendMode={props.$mixBlendMode}
      $marginBottom={props.$marginBottom}
      $opacity={props.$opacity}
      className={props.className}
    >
      <div className={styles.cardTextContent}>
        <h3>{props.title}</h3>
        <p>{props.description}</p>
      </div>
      <div className={styles.cardImgContainer}>
        {/* Art-directed so SSR picks the right crop (useWindowSize is 0 on
            the server) and the eager load fetches exactly one variant. */}
        <picture>
          <source
            media="(max-width: 767px)"
            srcSet={getSpeedsizeImageUrl(
              props.image.resp as string,
              props.image.width,
            )}
          />
          <img
            loading="eager"
            src={getSpeedsizeImageUrl(props.image.url, props.image.width)}
            alt={props.title as unknown as string}
            width={props.image.width}
            height={props.image.height}
          />
        </picture>
      </div>
    </StyledCapability>
  );
};

export const Capabilities = ({
  className,
  htmlId,
}: {
  className?: string;
  htmlId?: string;
}) => {
  const capabilities = useCapabilities();
    type CapabilityKey = keyof typeof capabilities;
    const [active, setActive] = useState<CapabilityKey>(
      () => Object.keys(capabilities)[0] as CapabilityKey,
    );

    const rootRef = useRef<HTMLDivElement>(null);
    const cardContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (active && cardContainerRef.current) {
        cardContainerRef.current.scrollTo({
          behavior: 'auto',
          left: 0,
        });
      }
    }, [active]);

    return (
      <section className={cx(styles.root, className)} id={htmlId} ref={rootRef}>
        <Container>
          {/* <div> */}
          <div className={styles.buttonRowsWrapper}>
            <CaretUp fill="#ffffff" />
            <div className={cx(styles.buttonRows, 'u-hide-scrollbar')}>
              {(
                Object.keys(capabilities) as Array<keyof typeof capabilities>
              ).map((key) => (
                <button
                  className={key === active ? 'active' : ''}
                  key={key}
                  onClick={(e) => {
                    analytics.track(`${capabilities[key].title} CLICK`);
                    setActive(key);
                    console.log(e);
                    if (window.innerWidth > 769) return;
                    (e.target as HTMLButtonElement).scrollIntoView({
                      behavior: 'smooth',
                      inline: 'center',
                      block: 'nearest',
                    });
                  }}
                >
                  {capabilities[key].title}
                </button>
              ))}
              {/* <div className="gradient"></div> */}
            </div>
          </div>
          <div className={styles.content}>
            {/* <div> */}
            <div className={styles.text}>
              <h3>{capabilities[active].heading}</h3>
              <p>{capabilities[active].description}</p>
            </div>
            <div
              // className={`features features-${capabilities[active].features.length} feature-${active}`}
              className={cx(styles.featuresContainer, 'u-hide-scrollbar')}
              ref={cardContainerRef}
              key={`features-${active}`}
            >
              {capabilities[active].features.map((feature, index) => {
                return (
                  <Capability
                    key={`capability-${active}-${index}`}
                    {...(feature as CapabilityProps)}
                    $delay={index / 20}
                  />
                );
              })}
            </div>
            {/* </div> */}
          </div>
          {/* </div> */}
        </Container>
      </section>
    );
};
